import { Badge } from "@/components/ui/badge";
import { CheckCircle2, AlertTriangle, XCircle, Circle, HelpCircle } from "lucide-react";

/**
 * Visualises an order's profitability vs its order_type's target_margin_percent.
 *
 * Props:
 *  - data: { amount_ht, material_cost, labor_cost, total_cost, net_margin,
 *           net_margin_percent, target_margin_percent, status }
 *  - compact: shorter label
 */
export const ProfitabilityBadge = ({ data, compact = false }) => {
  if (!data) return null;
  const { net_margin_percent, target_margin_percent, status } = data;

  const cfg = {
    above_target: { color: "bg-emerald-100 text-emerald-800 border-emerald-300", Icon: CheckCircle2, label: "Rentable" },
    near_target:  { color: "bg-amber-100 text-amber-800 border-amber-300", Icon: AlertTriangle, label: "Proche cible" },
    below_target: { color: "bg-red-100 text-red-800 border-red-300", Icon: AlertTriangle, label: "Sous cible" },
    negative:     { color: "bg-red-200 text-red-900 border-red-400 font-bold", Icon: XCircle, label: "Déficitaire" },
    no_data:      { color: "bg-slate-100 text-slate-600 border-slate-300", Icon: Circle, label: "Pas de données" },
  }[status] || { color: "bg-slate-100", Icon: HelpCircle, label: "—" };

  const { Icon } = cfg;
  const tooltip =
    status === "no_data"
      ? `Aucun coût saisi (matière + main-d'œuvre). Marge cible : ${target_margin_percent}%.`
      : `Marge nette : ${net_margin_percent}% — Cible : ${target_margin_percent}% • CA ${data.amount_ht} € • Matière ${data.material_cost} € • Main-d'œuvre ${data.labor_cost} € • Marge ${data.net_margin} €`;

  const valueText = status === "no_data" ? "" : `${net_margin_percent}% / ${target_margin_percent}%`;

  return (
    <Badge
      variant="outline"
      className={`${cfg.color} text-xs font-mono gap-1`}
      title={tooltip}
      data-testid={`profit-badge-${status}`}
    >
      <Icon className="h-3 w-3" />
      {compact ? valueText || cfg.label : `${cfg.label}${valueText ? ` · ${valueText}` : ""}`}
    </Badge>
  );
};

export default ProfitabilityBadge;
